import React, { useEffect, useRef, useState } from "react"
import styles from "./announcements.module.css"
import api from "../../services/api"
import Button from "../../components/Form/Button"
import Input from "../../components/Form/Input"
import Textarea from "../../components/Form/Textarea"
import Select from "../../components/Form/Select"
import ColorPicker from "../../components/Form/ColorPicker"
import Checkbox from "../../components/Form/Checkbox"
import FormGroup from "../../components/Form/FormGroup"
import Modal from "../../components/Modal"
import ModalDelete from "../../components/Modal/ModalDelete"
import AnnouncementModal, { categoryData, priorityLabels } from "../../components/AnnouncementModal"
import FormattedAnnouncementText from "../../components/AnnouncementModal/FormattedAnnouncementText"
import Loading from "../layouts/Loading"
import { toast } from "react-toastify"
import { MdCampaign, MdOutlineAdd, MdOutlineDeleteOutline, MdOutlineFileDownload, MdOutlineFileUpload, MdOutlineGroups, MdOutlineVisibility } from "react-icons/md"

const MAX_IMPORT_FILE_SIZE = 5 * 1024 * 1024

const categories = [
    { id: "info", value: "Informativo" },
    { id: "maintenance", value: "Manutenção" },
    { id: "billing", value: "Cobrança" },
    { id: "product", value: "Produto" },
    { id: "urgent", value: "Importante" },
]
const priorities = [
    { id: "normal", value: "Normal" },
    { id: "important", value: "Atenção" },
    { id: "critical", value: "Ação necessária" },
]
const visibilityOptions = [
    { id: "all", value: "Todos os usuários" },
    { id: "account_admins", value: "Somente administradores das contas" },
]
const moduleMatchOptions = [
    { id: "any", value: "Possui qualquer módulo selecionado" },
    { id: "all", value: "Possui todos os módulos selecionados" },
]
const linkTypes = [{ id: "internal", value: "Rota interna" }, { id: "external", value: "Link externo" }]
const accountStatusOptions = [{ id: "active", value: "Ativa" }, { id: "inactive", value: "Inativa" }]
const integrationOptions = [
    { id: "whatsapp", value: "WhatsApp Lite" },
    { id: "whatsapp_api", value: "WhatsApp API oficial" },
    { id: "direct", value: "Instagram Direct" },
    { id: "messenger", value: "Messenger" },
]
const listStatusOptions = [
    { id: "all", value: "Todos" },
    { id: "active", value: "Ativos" },
    { id: "archived", value: "Histórico" },
    { id: "draft", value: "Rascunhos" },
]
const publicationStatusOptions = [
    { id: "draft", value: "Rascunho" },
    { id: "published", value: "Publicado" },
]
const displayModeOptions = [
    { id: "modal", value: "Modal automático" },
    { id: "banner", value: "Banner persistente" },
    { id: "center", value: "Somente na Central" },
]
const dateValue = (value) => {
    if (!value) return ""
    const date = new Date(value)
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    return localDate.toISOString().slice(0, 16)
}
const isoDate = (value) => value ? new Date(value).toISOString() : null
const formatDate = (value) => value ? new Date(value).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : ""

const emptyForm = {
    title: "", summary: "", details: "", category: "info", priority: "normal", accent_color: "#6245ae",
    published_at: dateValue(new Date()), expires_at: "", is_published: true, requires_acknowledgement: false, display_mode: "modal",
    primary_action: { label: "", url: "", type: "internal" },
    audience: { visibility: "all", account_ids: [], excluded_account_ids: [], modules: [], module_match: "any", account_created_from: "", account_created_to: "", plan_ids: [], account_statuses: ["active"], integration_platforms: [], integration_match: "any", active_users_min: "", active_users_max: "" },
    renotify: false,
    internal_notes: "",
}

const Announcements = () => {
    const [announcements, setAnnouncements] = useState([])
    const [canManage, setCanManage] = useState(false)
    const [accountOptions, setAccountOptions] = useState([])
    const [moduleOptions, setModuleOptions] = useState([])
    const [planOptions, setPlanOptions] = useState([])
    const [audienceEstimate, setAudienceEstimate] = useState(null)
    const [simulationAccountId, setSimulationAccountId] = useState("")
    const [simulateAccountAdmin, setSimulateAccountAdmin] = useState(false)
    const [simulation, setSimulation] = useState(null)
    const [search, setSearch] = useState("")
    const [listStatus, setListStatus] = useState("all")
    const [loading, setLoading] = useState(false)
    const [showForm, setShowForm] = useState(false)
    const [deleteId, setDeleteId] = useState("")
    const [preview, setPreview] = useState(null)
    const [importPreview, setImportPreview] = useState(null)
    const [selectedImportIds, setSelectedImportIds] = useState([])
    const [importPreviewId, setImportPreviewId] = useState("")
    const [exportItems, setExportItems] = useState(null)
    const [selectedExportIds, setSelectedExportIds] = useState([])
    const [exportPreviewId, setExportPreviewId] = useState("")
    const [form, setForm] = useState(emptyForm)
    const importInputRef = useRef(null)
    const detailsInputRef = useRef(null)

    useEffect(() => { loadAnnouncements() }, [])
    useEffect(() => { if (canManage) loadAudienceOptions() }, [canManage])

    const loadAnnouncements = async () => {
        try {
            setLoading(true)
            const response = await api.get("/announcements")
            setAnnouncements(response?.data?.data || [])
            setCanManage(Boolean(response?.data?.can_manage))
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const loadAudienceOptions = async () => {
        try {
            const response = await api.get("/announcements/audience-options")
            const data = response?.data?.data || {}
            setAccountOptions((data.accounts || []).map(account => ({ id: account._id, value: `${account.name}${account.company?.fantasy_name ? ` · ${account.company.fantasy_name}` : ""}` })))
            setModuleOptions((data.modules || []).map(module => ({ id: module.action, value: module.name })))
            setPlanOptions((data.plans || []).map(plan => ({ id: plan._id, value: `${plan.name}${plan.is_active ? "" : " · inativo"}` })))
        } catch (err) { toast.error(err?.response?.data?.message) }
    }

    const resetAudienceReview = () => { setAudienceEstimate(null); setSimulation(null) }
    const openNew = () => { setForm(emptyForm); resetAudienceReview(); setSimulationAccountId(""); setShowForm(true) }
    const openEdit = (item) => {
        setForm({
            ...emptyForm, ...item,
            published_at: dateValue(item.published_at), expires_at: dateValue(item.expires_at),
            primary_action: { ...emptyForm.primary_action, ...(item.primary_action || {}) },
            audience: {
                ...emptyForm.audience, ...(item.audience || {}),
                account_ids: (item.audience?.account_ids || []).map(String),
                excluded_account_ids: (item.audience?.excluded_account_ids || []).map(String),
                account_created_from: dateValue(item.audience?.account_created_from),
                account_created_to: dateValue(item.audience?.account_created_to),
                plan_ids: (item.audience?.plan_ids || []).map(String),
                account_statuses: item.audience?.account_statuses || [],
                integration_platforms: item.audience?.integration_platforms || [],
                active_users_min: item.audience?.active_users_min ?? "",
                active_users_max: item.audience?.active_users_max ?? "",
            },
            renotify: false,
        })
        resetAudienceReview()
        setSimulationAccountId("")
        setShowForm(true)
    }
    const change = (field, value) => setForm(previous => ({ ...previous, [field]: value }))
    const changeAction = (field, value) => setForm(previous => ({ ...previous, primary_action: { ...previous.primary_action, [field]: value } }))
    const changeAudience = (field, value) => { resetAudienceReview(); setForm(previous => ({ ...previous, audience: { ...previous.audience, [field]: value } })) }
    const formatDetails = (prefix, suffix = prefix, placeholder = "texto") => {
        const textarea = detailsInputRef.current
        const start = textarea?.selectionStart ?? form.details.length
        const end = textarea?.selectionEnd ?? start
        const selected = form.details.slice(start, end) || placeholder
        const nextValue = `${form.details.slice(0, start)}${prefix}${selected}${suffix}${form.details.slice(end)}`
        change("details", nextValue)
        requestAnimationFrame(() => {
            textarea?.focus()
            textarea?.setSelectionRange(start + prefix.length, start + prefix.length + selected.length)
        })
    }
    const formatDetailsBlock = (prefix, placeholder) => {
        const textarea = detailsInputRef.current
        const start = textarea?.selectionStart ?? form.details.length
        const end = textarea?.selectionEnd ?? start
        const lineStart = form.details.lastIndexOf("\n", Math.max(start - 1, 0)) + 1
        const nextLineBreak = form.details.indexOf("\n", end)
        const lineEnd = nextLineBreak === -1 ? form.details.length : nextLineBreak
        const selected = form.details.slice(lineStart, lineEnd) || placeholder
        const formatted = selected.split("\n").map(line => `${prefix}${line || placeholder}`).join("\n")
        const nextValue = `${form.details.slice(0, lineStart)}${formatted}${form.details.slice(lineEnd)}`
        change("details", nextValue)
        requestAnimationFrame(() => textarea?.focus())
    }
    const payloadFromForm = () => ({
        ...form,
        published_at: isoDate(form.published_at),
        expires_at: isoDate(form.expires_at),
        audience: {
            ...form.audience,
            account_created_from: isoDate(form.audience.account_created_from),
            account_created_to: isoDate(form.audience.account_created_to),
        },
    })

    const estimateAudience = async () => {
        try {
            setLoading(true)
            const response = await api.post("/announcements/audience-preview", { audience: payloadFromForm().audience })
            setAudienceEstimate(response?.data?.data || { account_count: 0, user_count: 0, excluded_account_count: 0 })
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const simulateAudience = async () => {
        if (!simulationAccountId) return toast.error("Selecione uma conta para simular.")
        try {
            setLoading(true)
            const response = await api.post("/announcements/audience-simulate", { audience: payloadFromForm().audience, target_account_id: simulationAccountId, simulate_account_admin: simulateAccountAdmin })
            setSimulation(response?.data?.data || null)
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const save = async (event) => {
        event.preventDefault()
        if (form.is_published && !audienceEstimate) return toast.error("Estime e revise o público antes de publicar.")
        try {
            setLoading(true)
            const payload = { ...payloadFromForm(), audience_reviewed: Boolean(audienceEstimate) }
            const response = form._id ? await api.patch(`/announcements/${form._id}`, payload) : await api.post("/announcements", payload)
            toast.success(response?.data?.message)
            setShowForm(false)
            await loadAnnouncements()
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const duplicate = async (id) => {
        try {
            setLoading(true)
            const response = await api.post(`/announcements/${id}/duplicate`)
            toast.success(response?.data?.message)
            await loadAnnouncements()
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const remove = async (id) => {
        try {
            setLoading(true)
            const response = await api.delete(`/announcements/${id}`)
            toast.success(response?.data?.message)
            setDeleteId("")
            setAnnouncements(previous => previous.filter(item => item._id !== id))
        } catch (err) { toast.error(err?.response?.data?.message) } finally { setLoading(false) }
    }

    const closeExportModal = () => {
        setExportItems(null)
        setSelectedExportIds([])
        setExportPreviewId("")
    }

    const openExportModal = () => {
        if (!visibleAnnouncements.length) return toast.error("Nenhum comunicado encontrado no filtro atual para exportar.")
        setExportItems(visibleAnnouncements)
        setSelectedExportIds(visibleAnnouncements.map(item => item._id))
        setExportPreviewId(visibleAnnouncements[0]?._id || "")
    }

    const toggleExportSelection = (id) => setSelectedExportIds(previous => previous.includes(id) ? previous.filter(item => item !== id) : [...previous, id])
    const toggleAllExportItems = () => {
        const ids = (exportItems || []).map(item => item._id)
        setSelectedExportIds(previous => ids.length && ids.every(id => previous.includes(id)) ? [] : ids)
    }

    const exportAnnouncements = async () => {
        if (!selectedExportIds.length) return toast.error("Selecione pelo menos um comunicado para exportar.")
        try {
            setLoading(true)
            const response = await api.post("/announcements/export", { announcement_ids: selectedExportIds }, { responseType: "blob" })
            const disposition = response.headers?.["content-disposition"] || ""
            const fileName = disposition.match(/filename="?([^";]+)"?/i)?.[1] || `comunicados-oktto-${new Date().toISOString().slice(0, 10)}.json`
            const url = URL.createObjectURL(response.data)
            const link = document.createElement("a")
            link.href = url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(url)
            toast.success(`${selectedExportIds.length} comunicado(s) exportado(s) com sucesso!`)
            closeExportModal()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível exportar os comunicados.")
        } finally {
            setLoading(false)
        }
    }

    const selectImportFile = async (event) => {
        const file = event.target.files?.[0]
        event.target.value = ""
        if (!file) return
        if (file.size > MAX_IMPORT_FILE_SIZE) return toast.error("O arquivo deve ter no máximo 5 MB.")
        try {
            setLoading(true)
            const payload = JSON.parse(await file.text())
            const response = await api.post("/announcements/import-preview", payload)
            const data = response?.data?.data || {}
            const items = data.items || []
            const validIds = items.filter(item => item.valid).map(item => item.import_id)
            setImportPreview({ fileName: file.name, ...data, items })
            setSelectedImportIds(validIds)
            setImportPreviewId(items.find(item => item.valid)?.import_id || items[0]?.import_id || "")
        } catch (err) {
            toast.error(err instanceof SyntaxError ? "O arquivo selecionado não contém um JSON válido." : (err?.response?.data?.message || "Não foi possível validar o arquivo de comunicados."))
        } finally {
            setLoading(false)
        }
    }

    const closeImportModal = () => {
        setImportPreview(null)
        setSelectedImportIds([])
        setImportPreviewId("")
    }

    const toggleImportSelection = (item) => {
        if (!item?.valid) return
        setSelectedImportIds(previous => previous.includes(item.import_id) ? previous.filter(id => id !== item.import_id) : [...previous, item.import_id])
    }

    const toggleAllValidImportItems = () => {
        const validIds = (importPreview?.items || []).filter(item => item.valid).map(item => item.import_id)
        setSelectedImportIds(previous => validIds.length && validIds.every(id => previous.includes(id)) ? [] : validIds)
    }

    const importAnnouncements = async () => {
        const announcementsToImport = (importPreview?.items || [])
            .filter(item => item.valid && selectedImportIds.includes(item.import_id))
            .map(item => item.announcement)
        if (!announcementsToImport.length) return toast.error("Selecione pelo menos um comunicado válido para importar.")
        try {
            setLoading(true)
            const response = await api.post("/announcements/import", { format: "oktto-announcements", version: 1, announcements: announcementsToImport })
            toast.success(response?.data?.message)
            closeImportModal()
            await loadAnnouncements()
        } catch (err) {
            toast.error(err?.response?.data?.message || "Não foi possível importar os comunicados.")
        } finally {
            setLoading(false)
        }
    }

    const getTransferStatus = (item) => {
        if (!item?.is_published) return "Rascunho"
        if (item.archived || (item.expires_at && new Date(item.expires_at) <= new Date())) return "Histórico"
        if (new Date(item.published_at) > new Date()) return "Agendado"
        return "Publicado"
    }

    const selectedExportPreview = (exportItems || []).find(item => item._id === exportPreviewId) || exportItems?.[0]
    const selectedImportPreview = (importPreview?.items || []).find(item => item.import_id === importPreviewId) || importPreview?.items?.[0]

    const markPreviewSeen = async () => {
        const item = preview
        setPreview(null)
        if (item && !canManage) {
            await api.post(`/announcements/${item._id}/seen`, { acknowledged: item.requires_acknowledgement }).catch(() => {})
            if (!item.read && !item.archived) window.dispatchEvent(new CustomEvent("oktto:announcement-read", { detail: { announcement_id: item._id } }))
            loadAnnouncements()
        }
    }

    const visibleAnnouncements = announcements.filter(item => {
        const term = search.trim().toLocaleLowerCase("pt-BR")
        const matchesSearch = !term || `${item.title} ${item.summary}`.toLocaleLowerCase("pt-BR").includes(term)
        const matchesStatus = listStatus === "all"
            || (listStatus === "active" && item.is_published && !item.archived)
            || (listStatus === "archived" && item.archived)
            || (listStatus === "draft" && !item.is_published)
        return matchesSearch && matchesStatus
    })

    return (
        <div className={styles.container}>
            <div className={styles.topbar}>
                <div className={styles.topbarIntro}><strong>Comunicados da Oktto</strong><span>Avisos operacionais, comerciais e mudanças que merecem sua atenção.</span></div>
                {canManage && <div className={styles.topbarActions}>
                    <Button variant="secondary" fullWidth={false} onClick={openExportModal}><MdOutlineFileDownload /> Exportar JSON</Button>
                    <Button variant="secondary" fullWidth={false} onClick={() => importInputRef.current?.click()}><MdOutlineFileUpload /> Importar JSON</Button>
                    <input ref={importInputRef} className={styles.hiddenFileInput} type="file" accept="application/json,.json" onChange={selectImportFile} />
                    <Button variant="primary" fullWidth={false} onClick={openNew}><MdOutlineAdd /> Novo comunicado</Button>
                </div>}
            </div>
            <div className={styles.filters}>
                <Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Buscar por título ou resumo" />
                <Select options={listStatusOptions} selected={listStatus} onChange={value => setListStatus(value || "all")} showOptionAll={false} allowDeselectAll={false} />
            </div>
            <div className={styles.grid}>
                {visibleAnnouncements.map(item => {
                    const category = categoryData[item.category] || categoryData.info
                    const expired = item.archived || (item.expires_at && new Date(item.expires_at) <= new Date())
                    const scheduled = item.is_published && new Date(item.published_at) > new Date()
                    const status = !item.is_published ? "draft" : expired ? "expired" : scheduled ? "scheduled" : "published"
                    const statusLabel = { draft: "Rascunho", expired: "Encerrado", scheduled: "Agendado", published: "Publicado" }[status]
                    return <article key={item._id} className={styles.card} style={{ "--announcement-accent": item.accent_color || "#6245ae" }}>
                        <div className={styles.cardHeader}>
                            <span className={styles.category}>{category.icon}{category.label}</span>
                            <div className={styles.cardStatus}>
                                {canManage && <span className={`${styles.status} ${styles[status]}`}>{statusLabel}</span>}
                                <span className={styles.date}>{formatDate(item.published_at)}</span>
                            </div>
                        </div>
                        <div className={styles.content}><h3>{item.title}</h3><p>{item.summary}</p></div>
                        <div className={styles.meta}>
                            <span>{priorityLabels[item.priority] || priorityLabels.normal}</span>
                            {item.requires_acknowledgement && <span>Confirmação obrigatória</span>}
                            {!canManage && item.read && <span>Lido</span>}
                            {canManage && <span>{item.audience_estimate?.calculated_at ? `${item.audience_estimate.account_count} contas` : "Público não estimado"}</span>}
                            {canManage && <span>{item.metrics?.seen_count || 0} visualizações</span>}
                            {canManage && item.requires_acknowledgement && <span>{item.metrics?.acknowledged_count || 0} confirmações</span>}
                            {canManage && <span>Revisão {item.revision || 1}</span>}
                            {canManage && <span>{displayModeOptions.find(option => option.id === item.display_mode)?.value || "Modal automático"}</span>}
                        </div>
                        <div className={styles.actions}>
                            <Button variant="neutral" onClick={() => setPreview(item)}><MdOutlineVisibility /> Ver comunicado</Button>
                            {canManage && <><Button variant="neutral" onClick={() => duplicate(item._id)}>Duplicar</Button><Button variant="neutral" onClick={() => openEdit(item)}>Editar</Button><Button variant="danger" onClick={() => setDeleteId(item._id)}>Excluir</Button></>}
                        </div>
                    </article>
                })}
            </div>
            {!visibleAnnouncements.length && !loading && <div className={styles.empty}>Nenhum comunicado encontrado.</div>}

            {showForm && <Modal icon={<MdCampaign />} title={form._id ? "Editar comunicado" : "Novo comunicado"} description="Escreva o aviso e defina exatamente quem deve recebê-lo." closeModal={() => setShowForm(false)} fullWidith={true} bodyClassName={styles.modalBody}>
                <form className={styles.form} onSubmit={save}>
                    <section><div className={styles.sectionTitle}><strong>Mensagem</strong><span>Conteúdo apresentado ao usuário.</span></div>
                        <div className={styles.formGrid}><FormGroup labelText="Título"><Input value={form.title} onChange={e => change("title", e.target.value)} required /></FormGroup><FormGroup labelText="Categoria"><Select options={categories} selected={form.category} onChange={value => change("category", value || "info")} showOptionAll={false} allowDeselectAll={false} /></FormGroup><FormGroup labelText="Prioridade"><Select options={priorities} selected={form.priority} onChange={value => change("priority", value || "normal")} showOptionAll={false} allowDeselectAll={false} /></FormGroup><FormGroup labelText="Cor de destaque"><ColorPicker value={form.accent_color} onChange={value => change("accent_color", value)} label="Cor do comunicado" /></FormGroup></div>
                        <FormGroup labelText="Resumo"><Textarea rows={3} value={form.summary} onChange={e => change("summary", e.target.value)} required /></FormGroup>
                        <FormGroup labelText="Detalhes formatados">
                            <div className={styles.formattingToolbar} role="toolbar" aria-label="Formatar detalhes do comunicado">
                                <button type="button" onClick={() => formatDetails("**", "**", "texto em negrito")} title="Negrito"><strong>B</strong></button>
                                <button type="button" onClick={() => formatDetails("*", "*", "texto em itálico")} title="Itálico"><em>I</em></button>
                                <button type="button" onClick={() => formatDetails("__", "__", "texto sublinhado")} title="Sublinhado"><u>U</u></button>
                                <button type="button" onClick={() => formatDetails("~~", "~~", "texto tachado")} title="Tachado"><del>S</del></button>
                                <span />
                                <button type="button" onClick={() => formatDetailsBlock("## ", "Título da seção")} title="Título da seção">Título</button>
                                <button type="button" onClick={() => formatDetailsBlock("- ", "Item da lista")} title="Lista com marcadores">Lista</button>
                                <button type="button" onClick={() => formatDetails("[", "](https://exemplo.com)", "texto do link")} title="Link">Link</button>
                            </div>
                            <Textarea elementRef={detailsInputRef} rows={10} value={form.details} onChange={e => change("details", e.target.value)} placeholder="Use os botões de formatação ou escreva com marcação." />
                            <small className={styles.formattingHelp}>Use linhas em branco para separar parágrafos. A pré-visualização mostra o resultado final antes da publicação.</small>
                        </FormGroup>
                    </section>
                    <section><div className={styles.sectionTitle}><strong>Publicação</strong><span>Rascunhos não aparecem para os usuários. Publicados ficam agendados quando a data de publicação está no futuro.</span></div>
                        <div className={styles.formGrid}><FormGroup labelText="Status"><Select options={publicationStatusOptions} selected={form.is_published ? "published" : "draft"} onChange={value => change("is_published", value === "published")} showOptionAll={false} allowDeselectAll={false} /></FormGroup><FormGroup labelText="Publicar em"><Input type="datetime-local" value={form.published_at} onChange={e => change("published_at", e.target.value)} required /></FormGroup><FormGroup labelText="Encerrar em (opcional)"><Input type="datetime-local" value={form.expires_at} onChange={e => change("expires_at", e.target.value)} /></FormGroup><FormGroup labelText="Formato de exibição"><Select options={displayModeOptions} selected={form.display_mode} onChange={value => change("display_mode", value || "modal")} showOptionAll={false} allowDeselectAll={false} /></FormGroup></div>
                        <div className={styles.checks}><button type="button" onClick={() => change("requires_acknowledgement", !form.requires_acknowledgement)}><Checkbox checked={form.requires_acknowledgement} pink={true} /> Exigir “Li e estou ciente”</button>{form._id && <button type="button" onClick={() => change("renotify", !form.renotify)}><Checkbox checked={form.renotify} pink={true} /> Criar nova revisão e renotificar</button>}</div>
                        {form._id && form.renotify && <div className={styles.warning}>Todos os usuários elegíveis verão esta nova revisão, mesmo que já tenham lido a anterior.</div>}
                    </section>
                    <section><div className={styles.sectionTitle}><strong>Ação</strong><span>Link opcional para documentação, configuração ou suporte.</span></div>
                        <div className={styles.formGrid}><FormGroup labelText="Texto do botão"><Input value={form.primary_action.label} onChange={e => changeAction("label", e.target.value)} /></FormGroup><FormGroup labelText="Destino"><Input value={form.primary_action.url} onChange={e => changeAction("url", e.target.value)} /></FormGroup><FormGroup labelText="Tipo"><Select options={linkTypes} selected={form.primary_action.type} onChange={value => changeAction("type", value || "internal")} showOptionAll={false} allowDeselectAll={false} /></FormGroup></div>
                    </section>
                    <section><div className={styles.sectionTitle}><strong>Segmentação</strong><span>Todos os filtros preenchidos são combinados.</span></div>
                        <div className={styles.formGrid}><FormGroup labelText="Usuários"><Select options={visibilityOptions} selected={form.audience.visibility} onChange={value => changeAudience("visibility", value || "all")} showOptionAll={false} allowDeselectAll={false} /></FormGroup><FormGroup labelText="Regra dos módulos"><Select options={moduleMatchOptions} selected={form.audience.module_match} onChange={value => changeAudience("module_match", value || "any")} showOptionAll={false} allowDeselectAll={false} /></FormGroup></div>
                        <FormGroup labelText="Somente estas contas (vazio = todas)"><Select name="Contas incluídas" options={accountOptions} selected={form.audience.account_ids} onChange={value => changeAudience("account_ids", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                        <FormGroup labelText="Excluir estas contas"><Select name="Contas excluídas" options={accountOptions} selected={form.audience.excluded_account_ids} onChange={value => changeAudience("excluded_account_ids", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                        <FormGroup labelText="Módulos"><Select name="Módulos" options={moduleOptions} selected={form.audience.modules} onChange={value => changeAudience("modules", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                        <div className={styles.formGrid}>
                            <FormGroup labelText="Planos"><Select name="Planos" options={planOptions} selected={form.audience.plan_ids} onChange={value => changeAudience("plan_ids", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                            <FormGroup labelText="Status das contas"><Select name="Status das contas" options={accountStatusOptions} selected={form.audience.account_statuses} onChange={value => changeAudience("account_statuses", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                        </div>
                        <div className={styles.formGrid}>
                            <FormGroup labelText="Integrações conectadas"><Select name="Integrações" options={integrationOptions} selected={form.audience.integration_platforms} onChange={value => changeAudience("integration_platforms", value || [])} multiple={true} showOptionAll={false} /></FormGroup>
                            <FormGroup labelText="Regra das integrações"><Select options={moduleMatchOptions} selected={form.audience.integration_match} onChange={value => changeAudience("integration_match", value || "any")} showOptionAll={false} allowDeselectAll={false} /></FormGroup>
                        </div>
                        <div className={styles.formGrid}>
                            <FormGroup labelText="Mínimo de usuários ativos"><Input type="number" min="0" value={form.audience.active_users_min} onChange={event => changeAudience("active_users_min", event.target.value)} /></FormGroup>
                            <FormGroup labelText="Máximo de usuários ativos"><Input type="number" min="0" value={form.audience.active_users_max} onChange={event => changeAudience("active_users_max", event.target.value)} /></FormGroup>
                        </div>
                        <div className={styles.formGrid}><FormGroup labelText="Contas criadas a partir de"><Input type="datetime-local" value={form.audience.account_created_from} onChange={e => changeAudience("account_created_from", e.target.value)} /></FormGroup><FormGroup labelText="Contas criadas até"><Input type="datetime-local" value={form.audience.account_created_to} onChange={e => changeAudience("account_created_to", e.target.value)} /></FormGroup></div>
                        <div className={styles.audiencePreview}>
                            <Button type="button" variant="neutral" onClick={estimateAudience}><MdOutlineGroups /> Estimar e revisar público</Button>
                            {audienceEstimate && <div className={styles.estimateNumbers}><strong>{audienceEstimate.account_count} contas</strong><span>{audienceEstimate.user_count} usuários elegíveis</span><span>{audienceEstimate.excluded_account_count} contas fora do público</span></div>}
                        </div>
                        <div className={styles.simulator}>
                            <div className={styles.sectionTitle}><strong>Simular uma conta</strong><span>Confira se a conta seria incluída e quais regras foram avaliadas.</span></div>
                            <div className={styles.formGrid}>
                                <FormGroup labelText="Conta"><Select name="Conta para simular" options={accountOptions} selected={simulationAccountId} onChange={value => { setSimulationAccountId(value || ""); setSimulation(null) }} showOptionAll={false} /></FormGroup>
                                <button type="button" className={styles.simulateAdmin} onClick={() => { setSimulateAccountAdmin(previous => !previous); setSimulation(null) }}><Checkbox checked={simulateAccountAdmin} pink={true} /> Simular como administrador da conta</button>
                            </div>
                            <Button type="button" variant="neutral" onClick={simulateAudience}>Simular recebimento</Button>
                            {simulation && <div className={`${styles.simulationResult} ${simulation.matches ? styles.match : styles.noMatch}`}>
                                <strong>{simulation.matches ? "Esta conta receberia o comunicado" : "Esta conta não receberia o comunicado"}</strong>
                                <span>{simulation.facts?.active_users || 0} usuários ativos · Integrações: {(simulation.facts?.integrations || []).map(value => integrationOptions.find(option => option.id === value)?.value || value).join(", ") || "nenhuma conectada"}</span>
                                {simulation.reasons?.map(reason => <span key={reason.code}>{reason.matched ? "✓" : "×"} {reason.label}</span>)}
                            </div>}
                        </div>
                    </section>
                    <section><div className={styles.sectionTitle}><strong>Uso interno</strong><span>Não aparece para os clientes.</span></div><FormGroup labelText="Observações internas"><Textarea rows={3} value={form.internal_notes} onChange={e => change("internal_notes", e.target.value)} /></FormGroup></section>
                    <div className={styles.formActions}><Button type="button" variant="neutral" onClick={() => setPreview(payloadFromForm())}><MdOutlineVisibility /> Pré-visualizar</Button><Button type="button" variant="neutral" onClick={() => setShowForm(false)}>Cancelar</Button><Button type="submit" variant="primary">Salvar comunicado</Button></div>
                </form>
            </Modal>}
            {exportItems && <Modal icon={<MdOutlineFileDownload />} title="Exportar comunicados" description="Selecione quais comunicados devem fazer parte do arquivo JSON." closeModal={closeExportModal} fitContent={true} heightFitContent={true} bodyClassName={styles.transferModalBody}>
                <div className={styles.transferModal}>
                    <div className={styles.transferConfig}>
                        <div className={styles.transferSummary}>
                            <div><span>Disponíveis no filtro</span><strong>{exportItems.length}</strong></div>
                            <div><span>Selecionados</span><strong>{selectedExportIds.length}</strong></div>
                        </div>
                        <div className={styles.transferListHeader}>
                            <button type="button" onClick={toggleAllExportItems}>{exportItems.length > 0 && exportItems.every(item => selectedExportIds.includes(item._id)) ? "Desmarcar todos" : "Selecionar todos"}</button>
                            <span>O arquivo preserva conteúdo, formatação e segmentação.</span>
                        </div>
                        <div className={styles.transferList}>
                            {exportItems.map(item => <button key={item._id} type="button" className={exportPreviewId === item._id ? styles.active : ""} onClick={() => setExportPreviewId(item._id)}>
                                <span className={`${styles.selectionBox} ${selectedExportIds.includes(item._id) ? styles.checked : ""}`} onClick={event => { event.stopPropagation(); toggleExportSelection(item._id) }} aria-hidden="true" />
                                <div><strong>{item.title}</strong><span>{getTransferStatus(item)} · {categoryData[item.category]?.label || "Informativo"}</span></div>
                            </button>)}
                        </div>
                    </div>
                    <div className={styles.transferPreview}>
                        {selectedExportPreview ? <>
                            <div className={styles.transferPreviewHeader}><span>{getTransferStatus(selectedExportPreview)}</span><strong>{selectedExportPreview.title}</strong><small>{selectedExportPreview.summary}</small></div>
                            {selectedExportPreview.details && <FormattedAnnouncementText text={selectedExportPreview.details} className={styles.transferPreviewContent} />}
                        </> : <span className={styles.transferEmpty}>Selecione um comunicado para visualizar.</span>}
                    </div>
                    <div className={styles.transferActions}>
                        <Button variant="secondary" type="button" fullWidth={false} onClick={closeExportModal}>Cancelar</Button>
                        <Button variant="primary" type="button" fullWidth={false} onClick={exportAnnouncements} disabled={!selectedExportIds.length}><MdOutlineFileDownload /> Exportar {selectedExportIds.length || ""}</Button>
                    </div>
                </div>
            </Modal>}
            {importPreview && <Modal icon={<MdOutlineFileUpload />} title="Importar comunicados" description={importPreview.fileName || "Revise os comunicados antes de importar"} closeModal={closeImportModal} fitContent={true} heightFitContent={true} bodyClassName={styles.transferModalBody}>
                <div className={styles.transferModal}>
                    <div className={styles.transferConfig}>
                        <div className={`${styles.transferSummary} ${styles.importSummary}`}>
                            <div><span>No arquivo</span><strong>{importPreview.total_count || 0}</strong></div>
                            <div><span>Válidos</span><strong>{importPreview.valid_count || 0}</strong></div>
                            <div><span>Com problemas</span><strong>{importPreview.invalid_count || 0}</strong></div>
                            <div><span>Selecionados</span><strong>{selectedImportIds.length}</strong></div>
                        </div>
                        <div className={styles.transferListHeader}>
                            <button type="button" onClick={toggleAllValidImportItems}>{importPreview.valid_count > 0 && importPreview.items.filter(item => item.valid).every(item => selectedImportIds.includes(item.import_id)) ? "Desmarcar válidos" : "Selecionar válidos"}</button>
                            <span>Itens com problemas ficam bloqueados até o arquivo ser corrigido.</span>
                        </div>
                        <div className={styles.transferList}>
                            {importPreview.items.map(item => <button key={item.import_id} type="button" className={`${importPreviewId === item.import_id ? styles.active : ""} ${!item.valid ? styles.invalid : ""}`} onClick={() => setImportPreviewId(item.import_id)}>
                                <span className={`${styles.selectionBox} ${selectedImportIds.includes(item.import_id) ? styles.checked : ""} ${!item.valid ? styles.disabled : ""}`} onClick={event => { event.stopPropagation(); toggleImportSelection(item) }} aria-hidden="true" />
                                <div><strong>{item.announcement?.title || `Comunicado ${item.position}`}</strong><span>{item.valid ? `${categoryData[item.announcement?.category]?.label || "Informativo"} · Pronto para importar` : item.error}</span></div>
                                <small className={item.valid ? styles.validBadge : styles.invalidBadge}>{item.valid ? "Válido" : "Revisar"}</small>
                            </button>)}
                        </div>
                    </div>
                    <div className={styles.transferPreview}>
                        {selectedImportPreview ? <>
                            <div className={styles.transferPreviewHeader}>
                                <span className={selectedImportPreview.valid ? styles.validText : styles.invalidText}>{selectedImportPreview.valid ? "Pronto para importar" : "Não será importado"}</span>
                                <strong>{selectedImportPreview.announcement?.title || `Comunicado ${selectedImportPreview.position}`}</strong>
                                {selectedImportPreview.error && <small>{selectedImportPreview.error}</small>}
                                {selectedImportPreview.announcement?.summary && <small>{selectedImportPreview.announcement.summary}</small>}
                            </div>
                            {selectedImportPreview.announcement?.details && <FormattedAnnouncementText text={selectedImportPreview.announcement.details} className={styles.transferPreviewContent} />}
                        </> : <span className={styles.transferEmpty}>Nenhum comunicado disponível para visualizar.</span>}
                    </div>
                    <div className={styles.transferNotice}>Somente os comunicados válidos selecionados serão criados. Todos entrarão como novos rascunhos e nenhum usuário será notificado.</div>
                    <div className={styles.transferActions}>
                        <Button variant="secondary" type="button" fullWidth={false} onClick={closeImportModal}>Cancelar</Button>
                        <Button variant="primary" type="button" fullWidth={false} onClick={importAnnouncements} disabled={!selectedImportIds.length}><MdOutlineFileUpload /> Importar {selectedImportIds.length || ""} como rascunho</Button>
                    </div>
                </div>
            </Modal>}
            {deleteId && <ModalDelete closeModal={() => setDeleteId("")} onSubmit={remove} icon={<MdOutlineDeleteOutline />} title="Excluir comunicado" description="Somente comunicados sem nenhuma leitura podem ser excluídos. Deseja continuar?" id={deleteId} />}
            {preview && <AnnouncementModal announcement={preview} preview={canManage} onClose={markPreviewSeen} onAcknowledge={markPreviewSeen} />}
            {loading && <Loading fullPage={true} />}
        </div>
    )
}

export default Announcements
